import { FC, useEffect, useState } from 'react'

interface Props {
  /** Typeform ID to preload. If undefined the component renders nothing. */
  typeformId?: string
}

/**
 * Renders an off-screen, invisible Typeform iframe shortly after the landing
 * page becomes idle. The iframe loads the same form the onboarding step will
 * use, so its JS bundle, CSS, and fonts land in the browser cache before the
 * user clicks "Começar agora". When they do click, the questionnaire renders
 * almost instantly.
 *
 * The preload is gated on requestIdleCallback so it never competes with the
 * landing page's critical render.
 */
const TypeformPreloader: FC<Props> = ({ typeformId }) => {
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    if (!typeformId) return

    let idleHandle: number | undefined
    let timeoutHandle: number | undefined

    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number
        cancelIdleCallback?: (handle: number) => void
      }

    if (typeof win.requestIdleCallback === 'function') {
      idleHandle = win.requestIdleCallback(() => setShouldLoad(true), { timeout: 3000 })
    } else {
      timeoutHandle = window.setTimeout(() => setShouldLoad(true), 1500)
    }

    return () => {
      if (idleHandle !== undefined && typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle)
    }
  }, [typeformId])

  if (!shouldLoad || !typeformId) return null

  return (
    <iframe
      src={`https://form.typeform.com/to/${typeformId}`}
      title=""
      aria-hidden="true"
      tabIndex={-1}
      loading="eager"
      style={{
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
        width: '1px',
        height: '1px',
        opacity: 0,
        pointerEvents: 'none',
        border: 0
      }}
    />
  )
}

export default TypeformPreloader
